import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import client from '../sanity/client';
import '../components/SidebarMenu.scss';
import HamburgerButton from '../components/HamburgerButton';
import { useLanguage } from '../components/LanguageContext';

const SidebarMenu = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, switchLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [isMenuAnimated, setIsMenuAnimated] = useState(false);
  const isHome = location.pathname === '/';
  const [cheminTitle, setCheminTitle] = useState(null);
  const [expositionsTitle, setExpositionsTitle] = useState(null);
  const [ceramisteTitle, setCeramisteTitle] = useState(null);
  const [bolsTitle, setBolsTitle] = useState(null);
  const [chawansTitle, setChawansTitle] = useState(null);
  const [transmissionTitle, setTransmissionTitle] = useState(null);
  const [parutionsTitle, setParutionsTitle] = useState(null);
  const [veilleuseTitle, setVeilleuseTitle] = useState(null);

  const [subMenuItems, setSubMenuItems] = useState({
    chemin: [],
    ceramiste: [],
    poete: [],
    transmission: [],
  });

  const pageTitle = {
    fr: 'bols',
    en: 'bowls'
  };

  const poeteTitle = {
    fr: 'veilleuse de lieux',
    en: 'watcher'
  };

  // Récupération des titres depuis Sanity
  useEffect(() => {
    const fetchAllTitles = async () => {
      try {
        const results = await Promise.all([
          client.fetch(`*[_type == "chemin"][0]{ title }`),
          client.fetch(`*[_type == "expositions"][0]{ title }`),
          client.fetch(`*[_type == "ceramiste"][0]{ title }`),
          client.fetch(`*[_type == "subPageBols"][0]{ title }`),
          client.fetch(`*[_type == "subPageChawans"][0]{ title }`),
          client.fetch(`*[_type == "poeteSections"][0]{ sectionTitle }`),
          client.fetch(`*[_type == "poeteSectionParutions"][0]{ sectionTitle }`),
          client.fetch(`*[_type == "transmission"][0]{ title }`)
        ]);

        setCheminTitle(results[0]?.title);
        setExpositionsTitle(results[1]?.title);
        setCeramisteTitle(results[2]?.title);
        setBolsTitle(results[3]?.title);
        setChawansTitle(results[4]?.title);
        setVeilleuseTitle(results[5]?.sectionTitle);
        setParutionsTitle(results[6]?.sectionTitle);
        setTransmissionTitle(results[7]?.title);
      } catch (error) {
        console.error('Erreur lors de la récupération des titres:', error);
      }
    };

    fetchAllTitles();
  }, []);

  useEffect(() => {
    if (!isOpen) setActiveSection(null);
  }, [isOpen]);

  useEffect(() => {
    const fetchSubMenus = async () => {
      const query = `*[_type in ["subPageChemin", "subPageBols", "subPagePoete"]] | order(_createdAt asc){
        _type,
        title,
        "slug": slug.current
      }`;
      
      const queryTransmission = `*[_type == "subPageTransmission"] | order(order asc){
        _type,
        title,
        "slug": slug.current,
        order
      }`;
      
      const [items, transmissionItems] = await Promise.all([
        client.fetch(query),
        client.fetch(queryTransmission)
      ]);
      
      const groupedItems = {
        chemin: items.filter(item => item._type === 'subPageChemin'),
        ceramiste: items.filter(item => item._type === 'subPageBols'),
        poete: items.filter(item => item._type === 'subPagePoete'),
        transmission: transmissionItems, // Déjà triés par order dans la requête
      };
      
      setSubMenuItems(groupedItems);
    };

    fetchSubMenus();
  }, [location.pathname]);

  // Définir le menu actif selon le premier segment de l'URL
  useEffect(() => {
    const path = location.pathname.split('/')[1];
    setActiveMenu(path || null); // Ne pas forcer 'chemin' par défaut
  }, [location.pathname]);

  // Gestion du scroll sur mobile lorsque le menu est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [isOpen]);

  // Fermer le menu en cliquant en dehors (mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Seulement sur mobile et si le menu est ouvert
      if (window.innerWidth <= 768 && isOpen) {
        const menu = document.querySelector('.sidebar-menu');
        const hamburger = document.querySelector('.hamburger-button');
        
        // Si le clic n'est ni sur le menu ni sur le bouton hamburger
        if (menu && !menu.contains(event.target) && 
            hamburger && !hamburger.contains(event.target)) {
          closeMenu();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Animation en cascade du menu au chargement
  useEffect(() => {
    if (isHome) {
      // Délai plus long pour laisser l'image et le titre se charger
      const timer = setTimeout(() => {
        setIsMenuAnimated(true);
      }, 1500); // 1.5s après le chargement
      return () => clearTimeout(timer);
    } else {
      // Sur les autres pages, animation immédiate
      setIsMenuAnimated(true);
    }
  }, [isHome]);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  // Fonction pour fermer le menu seulement sur desktop ou pour les sous-menus
  const handleMainMenuClick = () => {
    // Sur mobile, ne pas fermer le menu pour les titres principaux
    if (window.innerWidth > 768) {
      closeMenu();
    }
    scrollToTop();
  };

  // Fonction pour gérer le scroll vers une ancre
  const handleSubPageClick = (anchorId, parentPage) => {
    if (location.pathname === `/${parentPage}`) {
      const element = document.getElementById(anchorId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/${parentPage}`, { state: { scrollTo: anchorId } });
    }
    closeMenu();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour générer le même ID que dans SubPage.js
  const getSectionId = (titleObj) => {
    // Toujours utiliser le français pour les ID
    const title = titleObj?.fr || titleObj?.en || titleObj || 'titre';
    return title.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .trim();
  };

  return (
    <>
      <HamburgerButton isOpen={isOpen} toggleMenu={toggleMenu} isHome={isHome} />
      
      <nav className={`sidebar-menu ${isOpen ? 'open' : ''} ${isHome ? 'home' : ''} ${isMenuAnimated ? 'menu-animated' : ''}`}>
        <div className="menu-content">
          {/* Menu principal */}
          <ul className="main-menu">
            {/* Chemin */}
            <li className="menu-item" style={{'--delay': '0'}}>
              <Link 
                to="/chemin"
                className={`menu_title ${location.pathname.includes('/chemin') ? 'active italic' : ''}`}
                onClick={handleMainMenuClick}
              >
                {cheminTitle?.[language] || cheminTitle?.fr || cheminTitle?.en || 'chemin'}
              </Link>
              {activeMenu === 'chemin' && (
                <ul className="sub-menu">
                  {subMenuItems.chemin.map((item) => (
                    <li key={item.slug}>
                      <a
                        href={`#${item.slug}`}
                        className={activeSection === item.slug ? 'italic' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          handleSubPageClick(item.slug, 'chemin');
                        }}
                      >
                        {item.title?.[language] || item.title?.fr || item.title?.en || item.slug}
                      </a>
                    </li>
                  ))}
                  <li>
                    <a
                      href="#expositions"
                      className={activeSection === 'expositions' ? 'italic' : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubPageClick('expositions', 'chemin');
                      }}
                    >
                      {expositionsTitle?.[language] || expositionsTitle?.fr || expositionsTitle?.en || 'expositions'}
                    </a>
                  </li>
                </ul>
              )}
            </li>

            {/* Ceramiste */}
            <li className="menu-item" style={{'--delay': '1'}}>
              <Link 
                to="/ceramiste"
                className={`menu_title ${location.pathname.includes('/ceramiste') ? 'active italic' : ''}`}
                onClick={handleMainMenuClick}
              >
                {ceramisteTitle?.[language] || ceramisteTitle?.fr || ceramisteTitle?.en || 'ceramiste'}
              </Link>
              {activeMenu === 'ceramiste' && (
                <ul className="sub-menu">
                  <li>
                    <Link to="/ceramiste/bols" className={location.pathname === '/ceramiste/bols' ? 'italic' : ''} onClick={closeMenu}>
                      {bolsTitle?.[language] || bolsTitle?.fr || bolsTitle?.en || pageTitle[language]}
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/ceramiste/chawans" 
                      className={location.pathname === '/ceramiste/chawans' ? 'italic' : ''}
                      onClick={closeMenu}
                    >
                      {chawansTitle?.[language] || chawansTitle?.fr || chawansTitle?.en || 'chawans'}
                    </Link>
                  </li>
                </ul>
              )}
            </li>

            {/* Transmission */}
            <li className="menu-item" style={{'--delay': '2'}}>
              <Link 
                to="/transmission"
                className={`menu_title ${location.pathname.includes('/transmission') ? 'active italic' : ''}`}
                onClick={handleMainMenuClick}
              >
                {transmissionTitle?.[language] || transmissionTitle?.fr || transmissionTitle?.en || 'transmission'}
              </Link>
              {activeMenu === 'transmission' && (
                <ul className="sub-menu">
                  {subMenuItems.transmission.map((item) => {
                    const sectionId = getSectionId(item.title);
                    return (
                      <li key={item.slug}>
                        <a
                          href={`#${sectionId}`}
                          className={activeSection === sectionId ? 'italic' : ''}
                          onClick={(e) => {
                            e.preventDefault();
                            handleSubPageClick(sectionId, 'transmission');
                          }}
                        >
                          {item.title?.[language] || item.title?.fr || item.title?.en || item.slug}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>

            {/* Veilleuse de lieux */}
            <li className="menu-item" style={{'--delay': '3'}}>
              <Link 
                to="/veilleusedelieux"
                className={`menu_title ${location.pathname.includes('/veilleusedelieux') ? 'active italic' : ''}`}
                onClick={handleMainMenuClick}
              >
                {poeteTitle?.[language] || poeteTitle?.fr || poeteTitle?.en || 'veilleuse de lieux'}
              </Link>
              {activeMenu === 'veilleusedelieux' && (
                <ul className="sub-menu">
                  <li>
                    <a
                      href="#parutions"
                      className={activeSection === 'parutions' ? 'italic' : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubPageClick('parutions', 'veilleusedelieux');
                      }}
                    >
                      {parutionsTitle?.[language] || parutionsTitle?.fr || parutionsTitle?.en || 'parutions'}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#résidences"
                      className={activeSection === 'résidences' ? 'italic' : ''}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSubPageClick('résidences', 'veilleusedelieux');
                      }}
                    >
                      {veilleuseTitle?.[language] || veilleuseTitle?.fr || veilleuseTitle?.en || 'résidences'}
                    </a>
                  </li>
                </ul>
              )}
            </li>

          </ul>

          {/* Menu du bas - séparé */}
          <div className="bottom-menu menu-item" style={{'--delay': '4'}}>
            <ul className="bottom-links">
              {/* Info */}
              <li>
                <Link 
                  to="/info" 
                  className={`menu_title ${location.pathname === '/info' ? 'active italic' : ''}`} 
                  onClick={closeMenu}
                >
                  info
                </Link>
              </li>
              
              {/* Contact */}
              <li>
                <a 
                  href="mailto:landaisclaire@gmail.com"
                  className="menu_title"
                  onClick={closeMenu}
                >
                  contact
                </a>
              </li>
              
              {/* Instagram */}
              <li>
                <a 
                  href="https://www.instagram.com/clairelandaissauvage/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="menu_title"
                  onClick={closeMenu}
                >
                  instagram
                </a>
              </li>
            </ul>

            {/* Sélecteur de langue */}
            <div className="language-switcher">
              <button 
                type="button"
                className={`menu_title ${language === 'fr' ? 'active italic' : ''}`}
                onClick={() => switchLanguage('fr')}
              >
                fr
              </button>
              <span className="menu_title"> / </span>
              <button 
                type="button"
                className={`menu_title ${language === 'en' ? 'active italic' : ''}`}
                onClick={() => switchLanguage('en')}
              >
                en
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {isOpen && <div className="overlay" onClick={closeMenu}></div>}
    </>
  );
};

export default SidebarMenu;